import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../services/api';
import BingoCard from '../components/BingoCard';
import ClaimModal from '../components/ClaimModal';
import ActivityFeed from '../components/ActivityFeed';
import RewardBanner from '../components/RewardBanner';
import UserGameStats from '../components/UserGameStats';
import BackButton from '../components/BackButton';
import BingoLetters from '../components/BingoLetters';
import { Game, BingoItem, BingoCard as BingoCardType } from '@bst-stellar/shared';
import { formatDate } from '../utils/dateFormatter';
import { isGameOver, getDaysRemaining } from '../utils/gameUtils';

export default function GameViewPage() {
    const { gameId } = useParams<{ gameId: string }>();
    const api = useApi();

    const [game, setGame] = useState<Game | null>(null);
    const [card, setCard] = useState<BingoCardType | null>(null);
    const [items, setItems] = useState<BingoItem[]>([]);
    const [claims, setClaims] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Claim modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<BingoItem | null>(null);
    const [selectedPosition, setSelectedPosition] = useState<{
        row: number;
        col: number;
    } | null>(null);
    const [selectedClaim, setSelectedClaim] = useState<any | null>(null);

    useEffect(() => {
        if (gameId) {
            loadGameData();
        }
    }, [gameId]);

    async function loadGameData() {
        try {
            setLoading(true);

            // Load game and items first
            const [gameResult, itemsResult] = await Promise.all([
                api.getGame(gameId!),
                api.getItems(),
            ]);

            setGame(gameResult.game);
            setItems(itemsResult.items);

            // Try to load card, but don't block if it fails
            try {
                const cardResult = await api.getBingoCard(gameId!);
                setCard(cardResult.card);

                // Load claims for this card
                if (cardResult.card) {
                    try {
                        const claimsResult = await api.getClaims();
                        // Filter claims for this specific card
                        const cardClaims = claimsResult.claims?.filter(
                            (claim: any) => claim.cardId === cardResult.card.id
                        ) || [];
                        setClaims(cardClaims);
                    } catch (claimError) {
                        console.warn('Failed to load claims:', claimError);
                        setClaims([]);
                    }
                }
            } catch (cardError) {
                console.warn('Failed to load bingo card:', cardError);
                setCard(null);
                setClaims([]);
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    const isGameEnded = game ? isGameOver(game) : false;

    function handleClaimClick(itemId: string, position: { row: number; col: number }) {
        if (isGameEnded) return;
        const item = items.find((i) => i.id === itemId);
        if (item) {
            // Find existing claim at this position (if any)
            const key = `${position.row},${position.col}`;
            const existingClaim = claims.find(
                (claim) => `${claim.position.row},${claim.position.col}` === key
            );

            setSelectedItem(item);
            setSelectedPosition(position);
            setSelectedClaim(existingClaim || null);
            setIsModalOpen(true);
        }
    }

    async function handleClaimSubmit(data: any) {
        // If editing a pending claim, update it instead of creating a new one
        if (selectedClaim?.status === 'pending') {
            await api.updateClaim(selectedClaim.id, {
                description: data.description,
                evidenceUrls: data.evidenceUrls,
                imageUrls: data.imageUrls,
            });
        } else {
            // Create new claim (for new claims or resubmitting rejected claims)
            await api.createClaim(data);
        }
        // Reload claims
        await loadGameData();
    }

    async function handleClaimDelete(claimId: string) {
        await api.deleteClaim(claimId);
        // Reload claims
        await loadGameData();
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-xl">Loading game...</div>
            </div>
        );
    }

    if (error || !game) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-2">Error</h1>
                    <p className="text-gray-600 mb-4">{error || 'Game not found'}</p>
                    <Link to="/" className="text-blue-600 hover:underline">
                        Return to Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            <div className="max-w-7xl mx-auto py-6 px-4">
                {/* Back Button */}
                <div className="mb-4">
                    <BackButton label="Back to Dashboard" to="/" />
                </div>

                {/* Game Header */}
                <div className="bg-surface rounded-lg shadow p-6 mb-6 border border-border">
                    <h1 className="text-3xl font-bold mb-3 text-text-primary">{game.name}</h1>
                    <div className="mb-4">
                        <BingoLetters gameId={gameId!} size="medium" />
                    </div>
                    {game.description && (
                        <p className="text-text-secondary mb-4">{game.description}</p>
                    )}
                    <div className="flex gap-4 text-sm text-text-secondary">
                        <span>
                            Start: {formatDate(game.startDate)}
                        </span>
                        {isGameOver(game) ? (
                            <span>Ended: {formatDate(game.endDate)}</span>
                        ) : (() => {
                            const days = getDaysRemaining(game.endDate);
                            if (days === null) return null;
                            if (days === 0) return <span className="font-semibold text-amber-600">⏰ Ends today!</span>;
                            return <span className="font-semibold text-primary">⏳ {days} day{days !== 1 ? 's' : ''} left</span>;
                        })()}
                        <span className="capitalize">Status: {game.status}</span>
                    </div>
                </div>

                {/* Game Ended Banner */}
                {isGameEnded && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-center">
                        <p className="text-red-800 font-semibold">🏁 This game has ended. No more submissions are accepted.</p>
                    </div>
                )}

                {/* Reward Banner */}
                {game.rewardBanner && (
                    <div className="mb-6">
                        <RewardBanner reward={game.rewardBanner} gameName={game.name} />
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Bingo Card */}
                    <div className="lg:col-span-2">
                        <div className="bg-surface rounded-lg shadow p-6 border border-border">
                            <h2 className="text-2xl font-bold mb-4 text-text-primary">Your Bingo Card</h2>
                            {card ? (
                                <BingoCard
                                    gridData={card.gridData}
                                    items={items}
                                    claims={claims}
                                    onClaimClick={handleClaimClick}
                                    readOnly={isGameEnded}
                                />
                            ) : (
                                <div className="text-center py-12 text-text-secondary">
                                    <p>No card generated yet</p>
                                    <p className="text-sm mt-2">
                                        Cards will be generated when the game starts
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1 space-y-6">
                        {/* User's own stats */}
                        {gameId && (
                            <UserGameStats
                                gameId={gameId}
                                gameStatus={game.status}
                                revealSchedule={game.revealSchedule}
                            />
                        )}

                        {/* Activity */}
                        <ActivityFeed gameId={gameId} limit={10} />
                    </div>
                </div>
            </div>

            {/* Claim Modal */}
            <ClaimModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                item={selectedItem}
                position={selectedPosition}
                cardId={card?.id || ''}
                existingClaim={selectedClaim}
                onSubmit={handleClaimSubmit}
                onDelete={handleClaimDelete}
                onUploadFile={api.uploadFile}
            />
        </div>
    );
}
